import React, { useRef } from 'react';
import clsx from 'clsx';
import Collapsible from '@dashboard/components/Collapsible';
import { ChevronDown, EllipsisVertical, GripVertical } from 'lucide-react';
import Button from '@dashboard/components/Button';
import GroupCard from '@dashboard/components/GroupCard';
import { Button as ShadcnButton, buttonVariants } from '@dashboard/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import { useTicketGroupEdit } from '../TicketGroupEditContext';
import { useTicketGroupHelpers } from '../useTicketGroupHelpers';
import TicketTypeEdit from './TicketTypeEdit';
import useTicketTypeHelpers from '../useTicketTypeHelpers';
import { Label } from '@dashboard/components/Form';
import TicketTypesList from './TicketTypesList';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import TicketGroupLayoutForm from './TicketGroupLayoutForm';
import TicketGroupSettingsForm from './TicketGroupSettingsForm';
import usePage from '@dashboard/hooks/usePage';
import { useTicketGroups } from '../TicketGroupsContext';
import StatusBadge from '@dashboard/components/StatusBadge';
import Tooltip from '@dashboard/components/Tooltip';
import { getGroupStatus, getGroupHiddenTooltip } from '../ticketGroupUtils';
import { TOOLTIP_DELAY_MS } from '@dashboard/components/ui/tooltip';

function checkCanDeleteTicketGroup(ticketGroup, page) {
  const hasSoldTicketTypes = ticketGroup.ticketTypes.some(
    ticketType => ticketType.quantitySold > 0,
  );

  if (hasSoldTicketTypes) {
    return {
      canDeleteTicketGroup: false,
      canDeleteTicketGroupMessage:
        "You can't delete this ticket group because it has made sales. You can archive it instead.",
    };
  }

  if (page.ticketGroups.length === 1) {
    return {
      canDeleteTicketGroup: false,
      canDeleteTicketGroupMessage:
        "You can't delete this group yet. Add tickets to another group first.",
    };
  }

  const ticketTypes = page.ticketGroups.flatMap(tg => tg.ticketTypes);
  const hasNoTicketTypesOutsideOfThisTicketGroup = ticketTypes.every(
    ticketType => ticketType.ticketGroup === ticketGroup._id,
  );

  if (hasNoTicketTypesOutsideOfThisTicketGroup) {
    return {
      canDeleteTicketGroup: false,
      canDeleteTicketGroupMessage:
        "You can't delete this group yet. Add tickets to another group first.",
    };
  }

  return {
    canDeleteTicketGroup: true,
    canDeleteTicketGroupMessage: '',
  };
}

export default function TicketGroupListItem({
  ticketGroup,
  dragHandleProps,
  isNewPageFlow = false,
  hasMultipleGroups = false,
  isLast = false,
  usePortal = false,
}) {
  const { page } = usePage();
  const {
    setIsEditingTicketGroup,
    isAddingTicketType,
    isEditingTicketType,
    editingTicketGroupId,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    editingTicketGroupMode,
  } = useTicketGroupEdit();

  const { state: ticketGroups } = useTicketGroups();

  const {
    onEnableDisableTicketGroup,
    onDuplicateTicketGroup,
    onDeleteTicketGroup,
    totalQuantitySoldInGroup,
  } = useTicketGroupHelpers(ticketGroup._id);

  const { onAddTicketType } = useTicketTypeHelpers();

  const settingsSubmitRef = useRef(null);
  const layoutSubmitRef = useRef(null);

  const enabledTicketTypesCount = ticketGroup.ticketTypes?.filter(
    ticketTypes => ticketTypes.status === 'enabled',
  ).length;

  const { canDeleteTicketGroup, canDeleteTicketGroupMessage } = checkCanDeleteTicketGroup(
    ticketGroup,
    page,
  );

  const isActive = editingTicketGroupId === ticketGroup._id;
  const isShowingTickets = isActive && editingTicketGroupMode === 'tickets';
  const isEditingSettings = isActive && editingTicketGroupMode === 'settings';
  const isEditingLayout = isActive && editingTicketGroupMode === 'layout';

  const activeTab = isActive ? editingTicketGroupMode : 'tickets';

  const { text: statusText, badge: statusBadge } = getGroupStatus({
    ticketGroup,
    page,
    allTicketGroups: ticketGroups,
  });
  const hiddenTooltip = getGroupHiddenTooltip({ ticketGroup, page, allTicketGroups: ticketGroups });
  const ticketCountText = `${enabledTicketTypesCount} ticket${enabledTicketTypesCount === 1 ? '' : 's'}`;
  const capacityText =
    ticketGroup?.capacity !== null ? `${ticketGroup.capacity} available` : 'Unlimited';
  const soldText = `${totalQuantitySoldInGroup || 0} sold`;

  const tabs = [
    { key: 'tickets', label: 'Tickets' },
    { key: 'settings', label: 'Settings' },
    ...(!isNewPageFlow ? [{ key: 'layout', label: 'Design' }] : []),
  ];

  function handleTabChange(tab) {
    if (tab === activeTab && isActive) return;
    setIsEditingTicketGroup(true, ticketGroup._id, tab);
  }

  return (
    <div
      className={clsx('w-full', isLast ? 'mb-4' : 'mb-2', {
        'opacity-50': ticketGroup.status === 'disabled',
      })}
    >
      <Collapsible.Root
        open={isActive}
        onOpenChange={open => {
          setIsEditingTicketGroup(open, ticketGroup._id, 'tickets');
        }}
      >
        <GroupCard
          variant="collapsible"
          className={clsx({
            'hover:bg-gray-50 dark:hover:bg-muted': !isActive,
            'bg-gray-50 dark:bg-card': isActive && hasMultipleGroups,
            'bg-white dark:bg-background': isActive && !hasMultipleGroups,
          })}
          headerClassName={clsx({
            'bg-gray-50 dark:bg-card': isActive && hasMultipleGroups,
            'bg-white dark:bg-background': isActive && !hasMultipleGroups,
          })}
          leading={
            <div
              className={clsx(
                buttonVariants({ variant: 'ghost', size: 'icon' }),
                'cursor-grab active:cursor-grabbing',
              )}
              {...dragHandleProps}
            >
              <GripVertical className="size-4" />
            </div>
          }
          wrapTitle={content => (
            <Collapsible.Trigger
              as="button"
              className={clsx(`py-3 truncate flex-1 min-w-0 text-left !ring-0 !focus:ring-0`, {
                'opacity-50 pointer-events-none': ticketGroup.status === 'archived',
              })}
              type="button"
            >
              {content}
            </Collapsible.Trigger>
          )}
          title={ticketGroup.name}
          summary={
            <>
              {ticketGroup.status === 'disabled' && (
                <StatusBadge status="disabled" label="Disabled" />
              )}
              {hiddenTooltip && (
                <Tooltip label={hiddenTooltip}>
                  <StatusBadge status="hidden" label="Hidden" />
                </Tooltip>
              )}
              {statusBadge && <StatusBadge status={statusBadge.status} label={statusBadge.label} />}
              <span>
                {statusText ? `${statusText} • ` : ''}
                {ticketCountText} • {capacityText} • {soldText}
              </span>
            </>
          }
          actions={
            <div className="shrink-0 flex items-center gap-2" onClick={e => e.stopPropagation()}>
              <button
                type="button"
                className={clsx(buttonVariants({ variant: 'ghost', size: 'icon' }))}
                onClick={() => {
                  setIsEditingTicketGroup(!isActive, ticketGroup._id, 'tickets');
                }}
              >
                <ChevronDown
                  className={clsx('size-4 transition-transform duration-200', {
                    'rotate-180': isActive,
                  })}
                />
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild disabled={ticketGroup.status === 'archived'}>
                  <ShadcnButton
                    variant="ghost"
                    size="icon"
                    aria-label="More options"
                    disabled={ticketGroup.status === 'archived'}
                  >
                    <EllipsisVertical className="size-4" />
                  </ShadcnButton>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={onDuplicateTicketGroup}>Duplicate</DropdownMenuItem>
                  <DropdownMenuItem onSelect={onEnableDisableTicketGroup}>
                    {ticketGroup.status === 'enabled' ? 'Disable' : 'Enable'}
                  </DropdownMenuItem>

                  {canDeleteTicketGroup ? (
                    <DropdownMenuItem onSelect={onDeleteTicketGroup} variant="destructive">
                      Delete
                    </DropdownMenuItem>
                  ) : (
                    <RadixTooltip.Provider delayDuration={TOOLTIP_DELAY_MS}>
                      <RadixTooltip.Root>
                        <RadixTooltip.Trigger asChild>
                          <DropdownMenuItem
                            disabled
                            onSelect={e => e.preventDefault()}
                            className="opacity-50"
                          >
                            Delete
                          </DropdownMenuItem>
                        </RadixTooltip.Trigger>

                        <RadixTooltip.Content
                          className={clsx(
                            'data-[state=delayed-open]:data-[side=top]:animate-slideDownAndFade data-[state=delayed-open]:data-[side=right]:animate-slideLeftAndFade data-[state=delayed-open]:data-[side=left]:animate-slideRightAndFade data-[state=delayed-open]:data-[side=bottom]:animate-slideUpAndFade select-none leading-none will-change-[transform,opacity]',
                            'inline-flex items-center rounded px-1.5 py-1.5 mx-2',
                            'bg-black dark:bg-gray-800 shadow z-50',
                          )}
                          side="left"
                        >
                          <RadixTooltip.Arrow className="fill-current text-black dark:text-gray-800 mx-auto" />
                          <span className="block text-sm text-white dark:text-gray-100 max-w-xs text-left">
                            {canDeleteTicketGroupMessage}
                          </span>
                        </RadixTooltip.Content>
                      </RadixTooltip.Root>
                    </RadixTooltip.Provider>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          }
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          showTabs={isActive}
        />

        {/* Settings tab - outside group border */}
        {isActive && isEditingSettings && (
          <div className="mt-2">
            <div className="flex flex-col">
              <div className="pt-2 mb-6">
                <TicketGroupSettingsForm
                  ticketGroup={ticketGroup}
                  onClose={() => {
                    setIsEditingTicketGroup(true, ticketGroup._id, 'tickets');
                  }}
                  formSubmit={({ handleSubmit }) => {
                    settingsSubmitRef.current = handleSubmit;
                    return null;
                  }}
                />

                <div className="mt-4 flex items-center justify-end gap-x-4">
                  {hasUnsavedChanges && (
                    <div className="text-gray-600 dark:text-muted-foreground text-sm">
                      Changes not saved
                    </div>
                  )}
                  <Button
                    appearance="secondary"
                    onClick={async () => {
                      setHasUnsavedChanges(false);
                      setIsEditingTicketGroup(true, ticketGroup._id, 'tickets');
                    }}
                    type="button"
                  >
                    Cancel
                  </Button>
                  <Button
                    appearance="primary"
                    onClick={() => {
                      settingsSubmitRef.current?.();
                    }}
                    type="button"
                  >
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Design tab - outside group border */}
        {isActive && isEditingLayout && !isNewPageFlow && (
          <div className="mt-2">
            <div className="flex flex-col">
              <div className="pt-2 mb-6">
                <TicketGroupLayoutForm
                  ticketGroup={ticketGroup}
                  onClose={() => {
                    setIsEditingTicketGroup(true, ticketGroup._id, 'tickets');
                  }}
                  formSubmit={({ handleSubmit }) => {
                    layoutSubmitRef.current = handleSubmit;
                    return null;
                  }}
                />

                <div className="mt-4 flex items-center justify-end gap-x-4">
                  {hasUnsavedChanges && (
                    <div className="text-gray-600 dark:text-muted-foreground text-sm">
                      Changes not saved
                    </div>
                  )}
                  <Button
                    appearance="secondary"
                    onClick={async () => {
                      setHasUnsavedChanges(false);
                      setIsEditingTicketGroup(true, ticketGroup._id, 'tickets');
                    }}
                    type="button"
                  >
                    Cancel
                  </Button>
                  <Button
                    appearance="primary"
                    onClick={() => {
                      layoutSubmitRef.current?.();
                    }}
                    type="button"
                  >
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tickets tab - outside group border */}
        {isActive && isShowingTickets && (
          <div className="mt-2">
            {ticketGroup.ticketTypes.length > 0 && (
              <div className="flex flex-col">
                <TicketTypesList
                  ticketTypes={ticketGroup.ticketTypes}
                  ticketGroupId={ticketGroup._id}
                  useCardStyle
                  usePortal={usePortal}
                />
              </div>
            )}

            {isAddingTicketType && (
              <div className="mt-4 mb-6">
                <Label>New ticket</Label>
                <TicketTypeEdit ticketGroupId={ticketGroup._id} />
              </div>
            )}

            {!isAddingTicketType && !isEditingTicketType && (
              <div className="mt-4 mb-4 flex justify-end">
                <Button appearance="primary" onClick={() => onAddTicketType(ticketGroup._id)}>
                  + Add ticket
                </Button>
              </div>
            )}
          </div>
        )}
      </Collapsible.Root>
    </div>
  );
}
